/**
 * The dashboard's run history — the same table the workflow cards used.
 *
 * The history is loaded when this tab is first opened rather than with the
 * page: most visits read numbers and never ask how they got there, and the
 * run list is a second request per dashboard.
 *
 * Columns are `RunHistoryTable`'s, unchanged. Cost is deliberately absent: the
 * backend records `cost_usd` on the run row but `WorkflowRun` does not carry it
 * on the wire, and a column of dashes is worse than no column.
 */

import React, { useEffect, useRef } from "react";

import RunHistoryTable from "../Workflows/RunHistoryTable";
import type { WorkflowRun } from "../../types/kpiWorkflow";

export interface KpiHistoryTabProps {
	workflowId: string;
	runs: WorkflowRun[] | undefined;
	loading: boolean;
	/** Read once when the tab opens; the notifier keeps live runs fresh after. */
	onLoad: (workflowId: string) => void;
	onCancelRun: (workflowId: string, runId: string) => void;
	/** Set the table to a run's version; settles once the restore has. */
	onRestoreRun: (workflowId: string, runId: string) => Promise<void>;
}

const KpiHistoryTab: React.FC<KpiHistoryTabProps> = ({
	workflowId,
	runs,
	loading,
	onLoad,
	onCancelRun,
	onRestoreRun,
}) => {
	// Once per workflow, not once per render of a callback identity: the read is
	// a request, and "when the tab opens" has to mean exactly that.
	const loadedFor = useRef<string | null>(null);
	useEffect(() => {
		if (loadedFor.current === workflowId) return;
		loadedFor.current = workflowId;
		onLoad(workflowId);
	}, [workflowId, onLoad]);

	// `undefined` is "not read yet" and `[]` is "read, and it has never run" —
	// the difference is what stops an empty state flashing before the answer.
	if (runs === undefined) {
		return (
			<p className="px-3 py-2 text-sm text-text-muted">
				{loading ? "Loading runs…" : "No run history yet."}
			</p>
		);
	}

	return (
		<div className="rounded-lg border border-gray-200 bg-white">
			<RunHistoryTable
				runs={runs}
				onCancelRun={(runId) => onCancelRun(workflowId, runId)}
				onRestoreRun={(runId) => onRestoreRun(workflowId, runId)}
			/>
		</div>
	);
};

export default KpiHistoryTab;
